// homePanelSet.js — the one decision behind "which home widgets does this
// workspace's `home_default_panels` actually authorise?"
//
// Extracted from pages/main.js's `_resolveWsPanelsInfo` because the whole bug
// lived in a single boolean buried in a 6000-line page module, where it could
// not be tested. Everything here is pure: the caller supplies the raw set, the
// ids the catalog currently knows, and whether a declared module's widgets are
// still in flight.
//
// The failure this exists to prevent: a workspace's panel set is AUTHORITATIVE
// — any widget it does not name is force-hidden. Bellforge's set was
// `['greeting', 'bf-overview']` and `bf-overview` exists nowhere in the
// codebase, so all 28 grid items were hidden and the home read as "widgets did
// not load", with nothing logged anywhere.

/**
 * @param {object}   args
 * @param {string[]} args.panels               raw `home_default_panels`
 * @param {Set<string>} args.knownIds          every id the ACTIVE workspace's
 *                                             catalog resolves (house set ∪ the
 *                                             live module/app widgets)
 * @param {boolean}  args.moduleWidgetsPending a module source is declared but
 *                                             its widgets have not landed yet
 * @returns {{ panels: string[]|null, ghosts: string[], authoritative: boolean }}
 *          `panels: null` means "treat as unset" — the caller falls through to
 *          the global default board rather than hiding everything.
 */
// Which of an installed app's declared widgets are ON BY DEFAULT — the
// generic install contract (2026-08-13 per Nika: "appstore apps come with
// widgets support — after installing they are just main part of the workspace
// workflow"). Before this, an additive install's widgets were force-hidden
// unless someone hand-pinned the workspace's `home_default_panels` (the
// app-mode compose deliberately never takes panels from the app), so every
// appstore install shipped ZERO visible widgets — verified live on
// natali/ws_EEYlHGTvZ8: 13 installed apps, no module widget rendered, and the
// Customize drawer couldn't reveal them either (the project gate blocks
// restore paths for ids the panel set doesn't name).
//
// The rule, per app stash ({ widgets, home_default_panels }):
//   • `home_default_panels` declared (manifest configDefaults) → the ids it
//     names THAT THE APP ITSELF DECLARES as widgets are default-on. The
//     intersection is the security posture: an app pins only its OWN widgets
//     — it can never flip a house widget or another app's (bi's declared
//     ['greeting','bi-revenue',…] contributes only the bi-* ids).
//   • no `home_default_panels` → ALL of the app's declared widgets are
//     default-on (widgets exist to be seen; an app opts a widget OUT by
//     declaring panels without it, e.g. bi omits bi-stock).
// Union across apps, deduped. Pure + sync — callers derive the stash from
// `root.workspaceApps` (live), so install/uninstall converge with zero
// per-workspace writes: the ids appear when the app's manifest lands and
// vanish when the ref is removed.
export const appDefaultWidgetIds = (apps) => {
  const out = new Set()
  for (const app of Array.isArray(apps) ? apps : []) {
    const widgets = Array.isArray(app && app.widgets) ? app.widgets : []
    if (!widgets.length) continue
    const declared = new Set(widgets.map((w) => w && w.id).filter(Boolean))
    const panels = Array.isArray(app && app.home_default_panels) ? app.home_default_panels : null
    const defaults = panels ? panels.filter((id) => declared.has(id)) : [...declared]
    for (const id of defaults) out.add(id)
  }
  return out
}

export const resolveHomePanelSet = ({ panels, knownIds, moduleWidgetsPending = false }) => {
  const raw = Array.isArray(panels) ? panels : []
  const known = knownIds instanceof Set ? knownIds : new Set(knownIds || [])

  const ghosts = raw.filter((id) => !known.has(id))

  // The question that matters: does this set name at least one REAL
  // non-greeting widget? The old test asked whether every entry was the
  // literal string `greeting`, which `['greeting', 'bf-overview']` passes.
  const namesSomethingReal = raw.some((id) => id !== 'greeting' && known.has(id))

  // Withhold the verdict while a declared module's widgets are in flight —
  // mid-flight a genuine project board names only ids the catalog does not
  // know yet, and judging it now would collapse it to the global default for a
  // beat and visibly flip back once they land.
  const authoritative = namesSomethingReal || moduleWidgetsPending

  // A set that is nothing but `greeting` still means "unset": a workspace whose
  // module was removed collapses to exactly that, and taken as authoritative it
  // would strand the home on greeting-only.
  const meaningful = raw.some((id) => id !== 'greeting')

  return {
    // Returned VERBATIM, never filtered: module widgets mount asynchronously
    // and must survive this pass untouched — the resync reveals them once
    // their nodes exist. Only the verdict consults the catalog.
    panels: authoritative && meaningful ? raw : null,
    ghosts,
    authoritative
  }
}

// WS-HOME-DRAWER-CANNOT-RESTORE-HIDDEN-MODULE-WIDGET-1 — "may the Customize
// drawer OFFER this widget, and may a click on it RESTORE the widget?"
//
// The defect this exists to remove: three paths judged the same module widget
// by three different rules. `_enforceProjectGate` (the RENDER gate) already
// exempts the APP PLANE — a widget declared by an app installed on THIS
// workspace (`settings.workspaceApps[]` / the singular `settings
// .workspaceModule` app ref) is a server-granted surface by construction. The
// drawer's list (`_drawerHiddenRows`) and the restore path (`_restoreWidget`)
// did not: they asked `home_default_panels` alone. An APP-MODE workspace
// carries no module ids in that set (the app-mode compose deliberately never
// takes panels from the app), so on Vault every `vault-*` widget was HIDEABLE
// (no grant check on hide) and UNRESTORABLE — the drawer listed only house
// widgets and "Reset layout to defaults" was the single way back, which
// destroys every other customisation.
//
// The grant set is therefore the SAME union the render gate uses:
//   not module-scoped at all (house widget)        → restorable
//   AI-created (server already scoped the row)     → restorable
//   declared by an app installed here (app plane)  → restorable
//   named by this workspace's `home_default_panels`→ restorable
//   otherwise (a TAKEOVER module's widget the panel set does not name)
//                                                  → BLOCKED, unchanged.
// The cross-workspace leak the gate exists to prevent is untouched: an id is
// exempt only while its app sits on this workspace's own install list.
//
// Pure + sync, so the rule is testable outside the 12k-line page module.
export const isWidgetRestorable = ({ id, scopedIds, appPlaneIds, aiIds, panels }) => {
  if (!id) return false
  const asSet = (v) => (v instanceof Set ? v : new Set(Array.isArray(v) ? v : []))
  if (!asSet(scopedIds).has(id)) return true
  if (asSet(aiIds).has(id)) return true
  if (asSet(appPlaneIds).has(id)) return true
  return Array.isArray(panels) && panels.includes(id)
}
